import { Link, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';

import Avatar from 'components/Avatar';
import Icon from 'components/base/Icon';
import { Tooltip } from 'components/base/Tooltip';

import Queries from 'graphql/Queries';

const Container = styled.div`
  align-items: center;
  align-self: center;
  background-color: var(--purpleTT);
  border-top-width: 0;
  bottom: 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  left: 0;
  margin-top: 0.75rem;
  padding: 0;
  position: relative;
  width: 100%;
  z-index: 2;

  ${props => props.envySidebar && css`
    align-self: flex-end;
    background-color: transparent;
    border-top: 0;
    bottom: initial;
    left: initial;
    margin: 0 auto;
    max-width: 16.5rem;
    padding: 0.125rem 0.75rem 1.5rem;
    position: relative;
  `}
  ${props => props.location === 'userbar' && css`
    display: none;
  `}
  ${props => props.location === 'mobile' && css`
    border-top: 2px solid rgba(var(--purple-gray-rgb), 0.5);
    color: rgba(var(--purple-gray-rgb), 0.5); // safari hack
    padding-top: 1.5rem;
  `}

  @media (min-width: 30em) {
    margin-top: 1rem;
  }
  @media (min-width: 38em) {
    ${props => props.envySidebar && css`
      margin: -3.9rem 0 0;
      max-width: initial;
      padding: 0.75em;
      width: 100%;
    `}
  }
  @media (min-width: 50em) {
    align-self: flex-end;
    border-top: 1px solid var(--gray);
    flex-direction: column;
    margin: 0;
    padding: 2.25rem 0.5rem;
    position: fixed;
    width: 8.5rem;
    z-index: initial;
    ${props => props.wide && css`
      flex-direction: row;
      padding: 1.125rem 1.5rem;
      width: 30%;
    `}
    ${props => props.envySidebar && css`
      border-top: 0;
      margin-top: initial;
      padding: 0.75em;
      width: 100%;
    `}
    ${props => props.location === 'userbar' && css`
      display: flex;
    `}
    ${props => props.location === 'mobile' && css`
      border-top: 0;
      color: initial;
      padding: 0;
    `}
  }
  @media (min-width: 60em) {
    ${props => props.envySidebar && css`
      align-self: center;
      border-top: 1px solid rgba(255, 255, 255, 0.5);
      bottom: 0;
      left: 0;
      padding: 1.125rem 1.5rem;
      position: fixed;
      width: 15.5rem;
    `}
  }
  @media (min-width: 69em) {
    ${props => props.isOnboarding && css`
      width: 18rem;
    `}
  }
  @media (min-width: 88em) {
    ${props => props.envySidebar && css`
      width: 18rem;
    `}
  }
`;

const User = styled.div`
  align-items: center;
  display: flex;
  justify-items: flex-start;
  @media (min-width: 50em) {
    flex-direction: column;
    ${props => props.wide && css`
      flex-direction: row;
    `}
  }
`;

const UserName = styled.h3`
  color: var(--purpleS);
  font-size: var(--step-down-1);
  margin: 0.4375rem 0 0.375rem 0.875rem;
  text-align: center;
  ${props => props.wide && css`
    margin-top: 0.75rem;
  `}
  ${props => props.envySidebar && css`
    color: rgba(255, 255, 255, 0.5);
    font-size: var(--step-down-1);
    margin-top: 0.1875rem;
  `}

  span {
    &:first-of-type {
      display: inline;
      ${props => props.envySidebar && css`
        display: inline;
      `}
    }
    &:nth-of-type(2) {
      display: none;
    }
  }

  @media (min-width: 50em) {
    color: var(--purple-gray);
    font-size: var(--step-down-2);
    margin-left: 0;
    ${props => props.wide && css`
      margin-left: 0.875rem;
    `}
    ${props => props.envySidebar && css`
      color: rgba(255, 255, 255, 0.5);
      font-size: var(--step-down-2);
      margin: 0.4375rem 0 0 0.875rem;
    `}

    span {
      &:first-of-type {
        display: none;
      }
      &:nth-of-type(2) {
        display: inline;
      }
    }
  }
  @media (min-width: 60em) {
    span:first-of-type {
      ${props => props.envySidebar && css`
        display: none;
      `}
    }
  }
`;

const Back = styled(({ envySidebar, wide, ...rest }) => <Link {...rest} />)`
  color: var(--purple-gray);
  cursor: pointer;
  display: block;
  height: 1.675rem;
  margin-bottom: -0.375rem;
  margin-top: 0;
  position: relative;
  transition: opacity 400ms var(--quintOut);
  width: 1.675rem;

  svg {
    height: 1.675rem;
    opacity: 0.5;
    width: 1.675rem;
  }

  @media (min-width: 50em) {
    height: 1.25rem;
    margin-top: 1.75rem;
    width: 1.25rem;
    ${props => props.wide && css`
      margin-top: 0;
    `}

    svg {
      height: 1.25rem;
      width: 1.25rem;
    }
  }
`;

const Logout = styled(({ envySidebar, ...rest }) => <Link {...rest} />)`
  color: ${props => (props.envySidebar && css`var(--white)`) || css`initial`};
  display: block;
  font-size: var(--step-down-1);
  margin-top: 0.125rem;
  opacity: 0.5;
  text-decoration: underline;
  transition: opacity 400ms var(--quintOut);
  &:hover {
    opacity: 1;
  }

  @media (min-width: 38em) {
    font-size: var(--step-down-2);
  }
  @media (min-width: 60em) {
    margin-top: 0.3125rem;
  }
`;

const TooltipAccount = Tooltip.extend`
  @media (hover: hover) and (min-width: 50em) {
    ${Back}:hover & {
      display: block;
    }
  }
`;

const Account = ({ envySidebar, isOnboarding, location }) => (
  <Container isOnboarding={isOnboarding} wide={(isOnboarding || envySidebar)} envySidebar={envySidebar} location={location}>
    <Queries.CurrentUser>
      {(user) => {
        if (user) {
          return (
            <React.Fragment>
              <User wide={(isOnboarding || envySidebar)} envySidebar={envySidebar}>
                <Avatar
                  alt=""
                  src={user.avatarUrl}
                  initials={user.initials}
                  size="small"
                  envySidebar={envySidebar}
                />
                <UserName wide={(isOnboarding || envySidebar)} envySidebar={envySidebar}>
                  {user.firstName}{' '}
                  <span>
                    {user.lastName}
                  </span>
                  <span>
                    {user.lastName.substring(0,1)}{'.'}
                  </span>
                </UserName>
              </User>
              {envySidebar || user.defaultProject ? (
                <Logout to="/sign-out" envySidebar={envySidebar}>
                  Sign Out
                </Logout>
              ) : (
                <Back to="/projects" wide={(isOnboarding || envySidebar)} envySidebar={envySidebar}>
                  <TooltipAccount>Return to Projects</TooltipAccount>
                  <Icon id="grid" title="grid" />
                </Back>
              )}
            </React.Fragment>
          );
        } else {
          return <Redirect to={{ pathname: '/sign-in' }} />;
        }
      }}
    </Queries.CurrentUser>
  </Container>
);

Account.defaultProps = {
  envySidebar: false,
  isOnboarding: false,
  wide: false,
};

Account.propTypes = {
  envySidebar: PropTypes.bool,
  isOnboarding: PropTypes.bool,
  wide: PropTypes.bool,
};

export default Account;



// WEBPACK FOOTER //
// ./src/layouts/shared/Account.js